import { createCheckoutSession } from "@/api/privateApi";
import Button, { CTAButton } from "@/components/Button";
import {
  navigationSquadBuilderIcon,
  navigationTacticsIcon,
} from "@/components/organisms/NavigationBar/NavBarTouchDevice/components/NavIcons";
import { mixpanelTrack } from "@/events/mixpanelTrack";
import { storeSignupContext } from "@/events/signupTracking";
import { useHighlightComponent } from "@/hooks/useHighlightComponent";
import { club } from "@/icons";
import { cn } from "@/lib/utils";
import {
  cleanTouchBarMenuZIndex,
  setTouchBarMenuZIndex,
} from "@/redux/globalState/globalStateSlice";
import { addAlert, showModal } from "@/redux/message/messageSlice";
import { AppDispatch } from "@/redux/store";
import { getUserSelector, setBuyIntent } from "@/redux/user/userSlice";
import { BREAKPOINTS } from "@/settings";
import {
  backendErrorCode,
  errorMessage,
  useTranslation,
} from "@repo/shared/i18n";
import { Dna } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router";
import { useMediaQuery } from "usehooks-ts";

import { USP } from "../LandingPages/Features/BaseLandingPage/USP";
import { sbcSolverIcon } from "../LandingPages/Features/SBCSolverLandingPage";

const FADE_OPACITY_MIN = 0;
const FADE_BRIGHTNESS_MIN = 0.15;

const LANDING_PAGE_TYPE = {
  boras: {
    creatorName: "Boras",
    promoCodeId: "promo_1SBlTJATqmUvIGSpc2qkDPUg",
    imageKey: "boras",
  },
  aj3: {
    creatorName: "AJ3",
    promoCodeId: "promo_1SBlTaATqmUvIGSpRDkgemUY",
    imageKey: "aj3",
  },
} as const;

export const CreatorLandingPage = () => {
  const { t } = useTranslation(["company", "common"]);
  const pageRef = useRef<HTMLDivElement>(null);
  const uvpRef = useRef<HTMLDivElement>(null);
  const { user } = useSelector(getUserSelector);
  const [fadeProgress, setFadeProgress] = useState(0);
  const { dismissHint, hintClassName } = useHighlightComponent();
  const dispatch = useDispatch<AppDispatch>();
  const isMobile = useMediaQuery(BREAKPOINTS.MOBILE.query);
  const navigate = useNavigate();
  const title = isMobile
    ? t("creator.hero.titleShort")
    : t("creator.hero.title");
  const [promoCode, setPromoCode] = useState("");
  const [creatorName, setCreatorName] = useState("");
  const [imageKey, setImageKey] = useState("");
  const searchParams = new URLSearchParams(window.location.search);
  const v = searchParams.get("v");

  const description = t("creator.hero.description", { creatorName });
  const descriptionMobile = t("creator.hero.descriptionMobile", {
    creatorName,
  });
  const usps = [
    {
      icon: <div className="size-6 touch-device:size-10 shrink-0">{club}</div>,
      title: t("creator.usps.clubImport.title"),
      description: (
        <div className="text-sm mobile:text-lg">
          {t("creator.usps.clubImport.description")}
        </div>
      ),
    },
    {
      icon: (
        <div className="size-6 mobile:size-10 shrink-0">
          {navigationSquadBuilderIcon}
        </div>
      ),
      title: t("creator.usps.squadBuilder.title"),
      description: (
        <div className="text-sm mobile:text-lg">
          {t("creator.usps.squadBuilder.description")}
        </div>
      ),
    },
    {
      icon: (
        <div className="size-6 mobile:size-10 shrink-0">
          <Dna className="size-6 mobile:size-10" />
        </div>
      ),
      title: t("creator.usps.evolutions.title"),
      description: (
        <div className="text-sm mobile:text-lg">
          {t("creator.usps.evolutions.description")}
        </div>
      ),
    },
    {
      icon: (
        <div className="size-6 mobile:size-10 shrink-0">
          {navigationTacticsIcon}
        </div>
      ),
      title: t("creator.usps.premiumTactics.title"),
      description: (
        <div className="text-sm touch-device:text-lg">
          {t("creator.usps.premiumTactics.description")}
        </div>
      ),
    },
    {
      icon: (
        <div className="size-6 mobile:size-10 shrink-0">{sbcSolverIcon}</div>
      ),
      title: t("creator.usps.sbcSolver.title"),
      description: (
        <div className="text-sm touch-device:text-lg">
          {t("creator.usps.sbcSolver.description")}
        </div>
      ),
    },
  ];

  useEffect(() => {
    if (v && LANDING_PAGE_TYPE[v]) {
      const { creatorName, promoCodeId, imageKey } = LANDING_PAGE_TYPE[v];
      setCreatorName(creatorName);
      setImageKey(imageKey);
      setPromoCode(promoCodeId);
    } else {
      mixpanelTrack("creator_landing_page_invalid_via_or_pt", { via: v });
      navigate("/");
    }
  }, []);

  const handleClickCTA = () => {
    // Add discount id when setup in Stripe

    dispatch(
      setBuyIntent({ subscriptionId: "diamond_monthly", promoCode: promoCode }),
    );

    if (!user?.uuid) {
      // The plain "login" modal doesn't set signup context itself (unlike the
      // "login-required" modal), so record it here — otherwise a signup started
      // from this page would inherit a stale trigger from an earlier gate.
      storeSignupContext("cta", {
        section: "Creator Landing Page",
        subsection: creatorName,
      });
      dispatch(showModal({ modalType: "login" }));
    } else if (user?.subscription === "free") {
      // create checkout session
      createCheckoutSession({
        priceId: "diamond_monthly",
        promoCode: promoCode,
      }).catch((error) =>
        dispatch(
          addAlert({
            description: errorMessage(backendErrorCode(error)),
            type: "error",
          }),
        ),
      );
    } else {
      dispatch(
        addAlert({
          description: t("creator.alreadyPaying"),
          type: "info",
        }),
      );
      navigate("/my-club");
    }
  };

  const handleScroll = useCallback(() => {
    const pageEl = pageRef.current;
    if (!pageEl) return;
    const pageTop = pageEl.getBoundingClientRect().top + window.scrollY;
    const scrollTop = Math.max(window.scrollY - pageTop, 0);
    const heroHeight = window.innerHeight;
    const progress = Math.min(scrollTop / heroHeight, 1);
    setFadeProgress(progress);
  }, []);

  const scrollToUVPs = useCallback(() => {
    dismissHint();
    uvpRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [dismissHint]);

  useEffect(() => {
    handleScroll();
    window.addEventListener("scroll", handleScroll, { passive: true });
    window.addEventListener("resize", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", handleScroll);
    };
  }, [handleScroll]);

  useEffect(() => {
    dispatch(setTouchBarMenuZIndex("z-30"));

    return () => {
      dispatch(cleanTouchBarMenuZIndex());
    };
  }, [dispatch]);

  return isMobile ? (
    <main
      ref={pageRef}
      className="bg-panel-base relative min-h-screen overflow-x-hidden pb-12"
    >
      <img
        className="fixed bottom-0 inset-x-0 m-auto z-0 opacity-10 pointer-events-none"
        src={"/logo.png"}
        alt={t("creator.logoAlt")}
      />
      <div className="relative z-20 flex flex-col min-h-screen px-6 pt-8 small-mobile:pt-12">
        <div className="flex flex-col gap-y-8 flex-1">
          <h1 className="text-4xl font-bold">{t("creator.hero.titleShort")}</h1>
          <h2 className="text-secondary/80 font-medium">{descriptionMobile}</h2>
          <div className="small-mobile:mt-6">
            <CTAButton
              onClick={handleClickCTA}
              label={t("creator.getOffNow")}
            />
          </div>
        </div>
        <div
          className={cn(
            "absolute bottom-20 left-1/2 -translate-x-1/2 z-30",
            hintClassName,
          )}
        >
          <Button
            label={t("common:learnMore")}
            variant="tertiary"
            onClick={scrollToUVPs}
          />
        </div>
      </div>
      <div
        ref={uvpRef}
        className="relative z-20 min-h-screen px-6 pt-10 pb-40 "
      >
        <h2 className="text-3xl font-semibold mb-10">
          {t("creator.whyEasySbc")}
        </h2>
        <ul className="flex flex-col gap-y-4">
          {usps.map((usp) => (
            <USP key={usp.title} {...usp} />
          ))}
        </ul>
        <div className="mt-10">
          <CTAButton
            onClick={handleClickCTA}
            label={t("creator.becomeUltimateNow")}
          />
        </div>
      </div>
      <img
        className="fixed bottom-0 left-1/2 -translate-x-1/2 w-4/5 z-10 pointer-events-none transition-[filter,opacity] duration-300 ease-out"
        src={`${import.meta.env.VITE_APP_ASSETS_BASE_URL}/creators/${imageKey}.png`}
        alt={t("creator.creatorAlt")}
        style={{
          opacity: 1 - fadeProgress * (1 - FADE_OPACITY_MIN),
          filter: `brightness(${1 - fadeProgress * (1 - FADE_BRIGHTNESS_MIN)})`,
        }}
      />
    </main>
  ) : (
    <main className="bg-panel-base relative overflow-x-hidden">
      <img
        className="fixed top-12 inset-x-0 m-auto z-0 opacity-5 pointer-events-none"
        src={"/logo.png"}
        alt={t("creator.logoAlt")}
      />
      <div className="relative z-20 flex flex-col min-h-[calc(100vh-50px)] px-12 large-desktop:px-16 pt-12 mx-auto max-w-content">
        <div className="flex flex-row">
          <div className="flex flex-col">
            <h1 className="text-4xl font-bold max-w-130">{title}</h1>
            <h2 className="text-secondary/80 font-medium mt-12 text-xl max-w-130">
              {description}
            </h2>
            <div className="mt-10">
              <CTAButton
                onClick={handleClickCTA}
                label={t("creator.getOffNow")}
              />
            </div>
          </div>
          <div className="flex flex-col mx-auto">
            <img
              className="max-w-96 max-h-96"
              src={`${import.meta.env.VITE_APP_ASSETS_BASE_URL}/creators/${imageKey}.png`}
            />
          </div>
        </div>
        <div className="mt-10 grid grid-cols-2 gap-6 max-w-296">
          {usps.map((usp) => (
            <USP key={usp.title} {...usp} />
          ))}
        </div>
      </div>
    </main>
  );
};
